'use client';

import {StepPayload} from '@blesk-frontend/api';
import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import {BoundingRect, Rects} from '../../../image-editor/components';
import {scaleRect} from '../../../image-editor/libs';
import {type Rect} from '../../../image-editor/types';
import {cn} from '../../../libs';
import {resolveAutoCropRect} from '../../libs/resolveCropRect';
import {type StepViewportUpdate} from './types';
import {type ImageRenderLayout, useImageRenderLayout} from './useImageRenderLayout';
import {useViewportUpdate} from './useViewportUpdate';
import {ZoomButtons} from './ZoomButtons';

const scaleForDisplay = (rect: Rect, layout: ImageRenderLayout): Rect =>
  scaleRect(
    {...rect, active: false},
    {x: layout.imageScale, y: layout.imageScale},
    {x: 0, y: layout.imageOffsetY},
  );

interface ImageRenderProps {
  className?: string;
  step: StepPayload;
  /**
   * Initial transformer scale.
   * @remarks
   * - use `0` for calculate initial scale
   * - if more than `0` use as initial scale
   */
  initialScale: number;
  zoomButtons?: boolean;
  scrollable?: boolean;
  fullSize?: boolean;
  /** Called only after a user zoom/pan gesture (not after mount auto-frame). */
  onUpdate?: (update: StepViewportUpdate) => void;
}

export const ImageRender = ({
  className,
  step,
  initialScale,
  zoomButtons = true,
  scrollable = false,
  fullSize = false,
  onUpdate,
}: ImageRenderProps) => {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [imageNaturalSize, setImageNaturalSize] = useState({width: 0, height: 0});
  const [isTransformInitialized, setIsTransformInitialized] = useState(false);
  const {layout: renderLayout, syncLayout} = useImageRenderLayout(ref, imageRef);
  const {finishUserTransform, onTransformed, startUserTransform} = useViewportUpdate(
    ref,
    imageRef,
    onUpdate,
  );

  const boundingId = `${step.id}-${id}`;
  const frameBoundingId = `frame-${boundingId}`;
  const frameTargetId = `rect-bounding-${frameBoundingId}`;

  const hasImageSize = imageNaturalSize.width > 0 && imageNaturalSize.height > 0;

  const savedScale =
    step.editor.zoom != null && Number.isFinite(step.editor.zoom) && step.editor.zoom > 0
      ? step.editor.zoom
      : null;
  const hasSavedTransform = savedScale != null && step.editor.pan != null;

  const autoScale =
    hasImageSize && renderLayout.frameWidth && renderLayout.frameHeight
      ? Math.min(
          renderLayout.frameWidth / imageNaturalSize.width,
          renderLayout.frameHeight / imageNaturalSize.height,
        )
      : 0;

  const autoCropRect = useMemo(() => {
    if (!hasImageSize) return null;

    return resolveAutoCropRect(step.editor.rects, imageNaturalSize.width, imageNaturalSize.height);
  }, [hasImageSize, imageNaturalSize.width, imageNaturalSize.height, step.editor.rects]);

  const initialScaleValue = useMemo(
    () => savedScale ?? (initialScale || autoScale),
    [savedScale, initialScale, autoScale],
  );

  const minScaleValue = Math.min(1, initialScaleValue);
  const maxScaleValue = Math.max(minScaleValue, initialScaleValue * 8);
  const isReady = hasImageSize && isTransformInitialized && renderLayout.imageScale > 0;
  const shouldRestoreTransform = !step.editor.viewport && hasSavedTransform;
  const frameCropRect = step.editor.viewport ?? (shouldRestoreTransform ? null : autoCropRect);

  const scaledRects = useMemo(
    () =>
      renderLayout.imageScale
        ? step.editor.rects.map((rect) => scaleForDisplay(rect as Rect, renderLayout))
        : [],
    [renderLayout, step.editor.rects],
  );

  const scaledFrameRect = useMemo(
    () =>
      frameCropRect && renderLayout.imageScale
        ? scaleForDisplay(
            {...frameCropRect, active: false, type: 'highlight'} as Rect,
            renderLayout,
          )
        : null,
    [frameCropRect, renderLayout],
  );

  useEffect(() => {
    if (imageNaturalSize.width && imageNaturalSize.height) return;

    const image = new Image();
    image.onload = () => {
      setImageNaturalSize({width: image.naturalWidth, height: image.naturalHeight});
    };
    image.src = step.src.original;
  }, [imageNaturalSize, step.src.original]);

  const onLoadImage = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const {naturalWidth, naturalHeight} = event.currentTarget;
      setImageNaturalSize({width: naturalWidth, height: naturalHeight});
      syncLayout();
    },
    [syncLayout],
  );

  const onInitTransformer = useCallback(() => {
    setIsTransformInitialized(true);
    syncLayout();
  }, [syncLayout]);

  // Restore an explicitly saved transform. Changes to the saved values are applied as well.
  useLayoutEffect(() => {
    if (!isReady || fullSize || !shouldRestoreTransform || !step.editor.pan) return;

    transformRef.current?.setTransform(step.editor.pan.x, step.editor.pan.y, savedScale, 0);
  }, [fullSize, isReady, savedScale, shouldRestoreTransform, step.editor.pan]);

  // Frame either the saved viewport or the canonical automatic crop.
  useLayoutEffect(() => {
    if (!isReady || fullSize || !scaledFrameRect) return;

    transformRef.current?.zoomToElement(frameTargetId, undefined, 0);
  }, [frameTargetId, fullSize, isReady, scaledFrameRect]);

  return (
    <div
      ref={ref}
      className={cn(
        'not-prose relative box-border w-full overflow-hidden rounded-md bg-bg-secondary transition-all',
        'aspect-video',
        fullSize && 'aspect-auto size-full',
        className,
      )}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        className="absolute -inset-20 size-full object-cover blur-3xl"
        src={step.src.cropped || step.src.original}
        alt=""
      />
      <div className="pointer-events-none absolute inset-0 z-20 rounded-[inherit] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]" />
      <div
        className={cn(
          'size-full translate-y-4 overflow-hidden rounded-[inherit] opacity-0 transition-all delay-100 will-change-transform',
          'flex items-center justify-center',
          isReady && 'translate-y-0 opacity-100',
        )}
      >
        {initialScaleValue > 0 && (
          <TransformWrapper
            ref={transformRef}
            initialScale={initialScaleValue}
            minScale={minScaleValue}
            maxScale={maxScaleValue}
            pinch={{
              step: 0.05,
            }}
            wheel={{
              step: 0.05,
            }}
            limitToBounds={true}
            disablePadding={true}
            disabled={!scrollable}
            panning={{
              velocityDisabled: true,
              allowLeftClickPan: scrollable,
              allowRightClickPan: false,
              disabled: false,
            }}
            smooth={false}
            onInit={onInitTransformer}
            onPanningStart={startUserTransform}
            onZoomStart={startUserTransform}
            onPanningStop={(transform) => finishUserTransform(transform.state)}
            onZoomStop={(transform) => finishUserTransform(transform.state)}
            onTransformed={onTransformed}
          >
            {scrollable && zoomButtons && (
              <ZoomButtons
                boundingId={boundingId}
                minScale={minScaleValue}
                maxScale={maxScaleValue}
                onUserTransformStart={startUserTransform}
              />
            )}

            <TransformComponent
              wrapperClass="relative"
              wrapperStyle={{
                width: '100%',
                height: '100%',
              }}
              contentStyle={
                fullSize
                  ? {
                      width: '100%',
                    }
                  : {}
              }
            >
              <img
                className={cn(fullSize && 'rounded-2xl drop-shadow-2xl')}
                ref={imageRef}
                src={step.src.original}
                alt=""
                onLoad={onLoadImage}
              />
              <Rects rects={scaledRects} boundingId={boundingId} edit={false} />
              {scaledFrameRect && (
                <BoundingRect rect={scaledFrameRect} boundingId={frameBoundingId} />
              )}
            </TransformComponent>
          </TransformWrapper>
        )}
      </div>
    </div>
  );
};
